import { type InputType, ProductTypes } from 'config'

export enum CreditScoreSelectionMethod {
  HighestMidScore = 'highestMidScore',
  LowestMidScore = 'lowestMidScore',
}

export const CreditScoreSelectionMethodLabels: Record<CreditScoreSelectionMethod, string> = {
  [CreditScoreSelectionMethod.HighestMidScore]: 'Highest Mid Score of all borrowers',
  [CreditScoreSelectionMethod.LowestMidScore]: 'Lowest Mid Score of all borrowers',
}

export interface CreditReportDefaultsRules {
  twoScoresUseLower: boolean
  oneScoreUseAsMid: boolean
  noScoreAsNA: boolean
  noScoreAsLesserWhenHighest: boolean
  noScoreAsLesserWhenLowest: boolean
}

export interface CreditReportDefaultsConfig {
  byProductType: Record<string, CreditScoreSelectionMethod>
  rules: CreditReportDefaultsRules
}

export const defaultCreditReportDefaultsConfig = (): CreditReportDefaultsConfig => ({
  byProductType: {
    [ProductTypes.Bridge]: CreditScoreSelectionMethod.LowestMidScore,
    [ProductTypes.FixAndFlip]: CreditScoreSelectionMethod.LowestMidScore,
    [ProductTypes.GroundUpConstruction]: CreditScoreSelectionMethod.LowestMidScore,
    [ProductTypes.DSCR]: CreditScoreSelectionMethod.LowestMidScore,
    [ProductTypes.NonQM]: CreditScoreSelectionMethod.LowestMidScore,
  },
  rules: {
    twoScoresUseLower: true,
    oneScoreUseAsMid: true,
    noScoreAsNA: true,
    noScoreAsLesserWhenHighest: true,
    noScoreAsLesserWhenLowest: true,
  },
})

export const creditScoreSelectionOptions: Record<string, string> = {
  [CreditScoreSelectionMethod.HighestMidScore]:
    CreditScoreSelectionMethodLabels[CreditScoreSelectionMethod.HighestMidScore],
  [CreditScoreSelectionMethod.LowestMidScore]:
    CreditScoreSelectionMethodLabels[CreditScoreSelectionMethod.LowestMidScore],
}

export const productTypeSelectInputs = (optionsMap: Record<string, string>): Record<string, InputType> => {
  const inputs: Record<string, InputType> = {}
  Object.entries(optionsMap).forEach(([productType, title]) => {
    inputs[productType] = {
      inputType: 'select',
      title: title || productType,
      options: creditScoreSelectionOptions,
      hasDefaultOption: false,
      span: 1,
    }
  })
  return inputs
}

export const ruleInputs = (): Record<string, InputType> => ({
  twoScoresUseLower: {
    inputType: 'checkbox',
    title: 'When borrower has just 2 scores, use the lower of the 2 as the Mid score',
    span: 'full',
  },
  oneScoreUseAsMid: {
    inputType: 'checkbox',
    title: 'When borrower has just 1 score, use the one score as the Mid Score',
    span: 'full',
  },
  noScoreAsNA: {
    inputType: 'checkbox',
    title: 'When borrower has No Score, use N/A as the Mid Score',
    span: 'full',
  },
  noScoreAsLesserWhenHighest: {
    inputType: 'checkbox',
    title: 'When borrower has No Score and Highest Mid Score of All Borrowers is chosen, N/A = The Lesser Score',
    span: 'full',
  },
  noScoreAsLesserWhenLowest: {
    inputType: 'checkbox',
    title: 'When borrower has No Score and Lowest Mid Score of All Borrowers is chosen, N/A = The Lesser Score',
    span: 'full',
  },
})
